<template>
  <div class="nav-header">
    <div class="nav-header__inner">

      <!-- ── MOBILE BAR (≤639px only) ────────────────────────────────────────
           All open/close state lives in default.vue as `activeMobile`.
           Clicking any icon emits 'set-mobile' with its key; default.vue
           toggles: same key closes it, different key closes previous and opens new.
      ──────────────────────────────────────────────────────────────────────── -->
      <div class="nav-header__mobile">
        <!-- Left hamburger → opens Leftbar drawer -->
        <button class="nav-header__mob-btn" @click="$emit('set-mobile', 'left')" aria-label="Open menu"
          v-tooltip.bottom="'Menu'">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="5"  width="18" height="2" rx="1" fill="currentColor"/>
            <rect x="3" y="11" width="18" height="2" rx="1" fill="currentColor"/>
            <rect x="3" y="17" width="18" height="2" rx="1" fill="currentColor"/>
          </svg>
        </button>

        <!-- Centre: brand logo -->
        <div class="nav-header__mob-logo" @click="$router.push('/')">
          <LaunchPadLogo :isDark="isDarkMode" class="nav-header__mob-logo-img" />
        </div>

        <!-- Right actions -->
        <div class="nav-header__mob-actions">
          <!-- Analytics / page-info panel toggle.
               Icon: info circle (ⓘ) — universal for "page details".
               Tooltip shows current route name + following/followers count. -->
          <button v-if="isUserAuth"
            class="nav-header__mob-btn" :class="{ 'nav-header__mob-btn--active': activeMobile === 'analytics' }"
            @click="$emit('set-mobile', 'analytics')"
            :aria-label="pageTitle"
            v-tooltip.bottom="pageTitle">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
              <path d="M12 16v-4M12 8h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>

          <!-- Search icon → toggles search panel -->
          <button v-if="isUserAuth"
            class="nav-header__mob-btn" :class="{ 'nav-header__mob-btn--active': activeMobile === 'search' }"
            @click="$emit('set-mobile', 'search')"
            aria-label="Search"
            v-tooltip.bottom="'Search'">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>

          <!-- Notification bell — tooltip shows unread count when > 0 -->
          <router-link v-if="isUserAuth" to="/notifications"
            class="nav-header__mob-btn notif-btn" :class="{ 'bell-shake': isShaking }"
            v-tooltip.bottom="unreadNotificationsCount > 0 ? 'Notifications (' + (unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount) + ')' : 'Notifications'">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span v-if="unreadNotificationsCount > 0" class="notif-btn__badge notif-btn__badge--mobile">
              {{ unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount }}
            </span>
          </router-link>

          <!-- MOBILE CHANGE 1c: Avatar now opens profile-switcher dropdown (same as desktop list).
               People icon below still opens the rightbar drawer. -->
          <!-- <Dropdown
            v-if="isUserAuth && userdata"
            style="border: none !important"
            :pt="{ panel: { class: 'header-dropdown-panel mob-profile-panel' } }"
            class="current-org-drops mob-profile-dropdown"
            v-model="selectedProfile"
            :options="filteredOrgs"
            optionLabel="name"
            @change="setCurrentProfile(selectedProfile)"
            placeholder="Select profile"
          >
            <template #value>
              <div class="nav-header__mob-avatar">
                <BaseAvatarNew
                  :storage-id="selectedProfile?.avatarFile?.storageId"
                  classes="header-avatar"
                  :rating="userdata?.rating"
                />
                <img v-if="badgeIconSrc" :src="badgeIconSrc" alt="badge" class="nav-header__mob-badge" />
              </div>
            </template>
            <template #option="slotProps">
              <div class="flex items-center gap-2">
                <BaseAvatarNew :storage-id="slotProps.option?.avatarFile?.storageId" class="header-dropdown-avatar" :rating="slotProps.option?.rating" />
                <span class="dropdown-menu__caption">{{ slotProps.option.username }}</span>
              </div>
            </template>
          </Dropdown> -->
          <!-- Mobile profile link: avatar + badge → goes to profile page -->
          <div
            v-if="isUserAuth && userdata"
            class="nav-header__mob-profile-link"
            style="cursor: pointer"
            @click="userdata.username && $router.push(`/user/${userdata.username}`)"
          >
            <div class="nav-header__mob-avatar">
              <BaseAvatarNew
                :storage-id="userdata?.avatarFile?.storageId"
                classes="header-avatar"
                :rating="userdata?.rating"
              />
              <img v-if="badgeIconSrc" :src="badgeIconSrc" alt="badge" class="nav-header__mob-badge" />
            </div>
          </div>

          <!-- Right hamburger (people icon) → opens Rightbar drawer -->
          <button class="nav-header__mob-btn" :class="{ 'nav-header__mob-btn--active': activeMobile === 'right' }"
            @click="$emit('set-mobile', 'right')" aria-label="Open discover panel"
            v-tooltip.bottom="'Discover'">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="9"  cy="7"  r="4" stroke="currentColor" stroke-width="2"/>
              <circle cx="17" cy="9"  r="3" stroke="currentColor" stroke-width="2"/>
              <path d="M1 21v-1a8 8 0 0 1 16 0v1" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <path d="M17 13a6 6 0 0 1 6 6v1" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Analytics panel: full-width, below nav-header, mobile only -->
      <transition name="nav-header-slide">
        <div v-if="activeMobile === 'analytics'" class="nav-header__mob-panel sm:hidden">
          <div class="nav-header__page-info">
            <h1 class="nav-header__page-title">{{ pageTitle }}</h1>
            <div class="nav-header__connections" v-if="userdata">
              <span class="nav-header__connections-sep">|</span>
              <router-link :to="userdata?.id ? `/followers/${userdata.id}` : '/'" class="nav-header__conn-link"
                @click="$emit('set-mobile', '')">
                <span class="nav-header__conn-count">{{ followingCount }}</span>
                <span class="nav-header__conn-label">Following</span>
              </router-link>
              <router-link :to="userdata?.id ? `/following/${userdata.id}` : '/'" class="nav-header__conn-link"
                @click="$emit('set-mobile', '')">
                <span class="nav-header__conn-count">{{ followersCount }}</span>
                <span class="nav-header__conn-label">Followers</span>
              </router-link>
            </div>
          </div>
        </div>
      </transition>

      <!-- Search panel: full-width search bar below nav-header, mobile only -->
      <transition name="nav-header-slide">
        <div v-if="activeMobile === 'search'" class="nav-header__mob-panel nav-header__mob-panel--search sm:hidden" @click.stop>
          <div class="nav-header-search nav-header-search--fullwidth">
            <input type="text" v-model="searchText" @input="() => fetchSearchResults()"
              placeholder="Search..." class="nav-header-search__input"
              ref="mobileSearchRef" @click.stop />
            <button class="nav-header-search__btn" @click.stop="() => fetchSearchResults()">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="8" stroke="white" stroke-width="2.2"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="white" stroke-width="2.2" stroke-linecap="round"/>
              </svg>
            </button>
            <!-- Results dropdown, shown while a search request is loading or a query is active -->
            <div v-if="isLoading || isSearchActive" class="nav-header-search__dropdown" @click.stop>
              <!-- Loading spinner while the debounced search request is in flight -->
              <div v-if="isLoading" class="flex justify-center items-center py-6">
                <ProgressSpinner style="width:36px;height:36px;margin:0 auto" strokeWidth="5" fill="transparent" animationDuration=".8s"/>
              </div>
              <!-- Once loaded, render grouped results: people, posts, groups -->
              <template v-else>
                <!-- People results -->
                <div>
                  <h4 class="search-section-title">People ({{ allUsers.length }})</h4>
                  <div v-if="allUsers.length">
                    <!-- One SuggestedPost row per matched user; closing it also closes the mobile search panel -->
                    <div v-for="user in displayedUsers" :key="user.id" class="mb-2">
                      <SuggestedPost :data="user" type="user" :onCloseDropdown="() => { closeSearchDropdown(); $emit('set-mobile', ''); }" />
                    </div>
                  </div>
                  <!-- Empty-state message when no users match -->
                  <p v-else class="search-empty-message">User not found</p>
                </div>
                <!-- Post results, only shown when there are matches -->
                <div v-if="allPosts.length" class="mt-3">
                  <h4 class="search-section-title">Posts ({{ allPosts.length }})</h4>
                  <div v-for="post in displayedPosts" :key="post.id" class="mb-2"
                    @click="closeSearchDropdown(); $emit('set-mobile', '')">
                    <SuggestedPostCompact :post="post" :onCloseDropdown="() => { closeSearchDropdown(); $emit('set-mobile', ''); }" />
                  </div>
                </div>
                <!-- Group results -->
                <div class="mt-3">
                  <h4 class="search-section-title">Groups ({{ allGroups.length }})</h4>
                  <div v-if="allGroups.length">
                    <!-- One SuggestedGroupItem per matched group, with an inline follow button -->
                    <div v-for="group in limitedGroups" :key="group.id" class="mb-2"
                      @click="closeSearchDropdown(); $emit('set-mobile', '')">
                      <SuggestedGroupItem :group="group" :showFollowButton="true" @follow="followGroup(group)" />
                    </div>
                  </div>
                  <!-- Empty-state message when no groups match -->
                  <p v-else class="search-empty-message">Group not found</p>
                </div>
              </template>
            </div>
          </div>
        </div>
      </transition>
      <!-- ── END MOBILE BAR ──────────────────────────────────────────────── -->

      <!-- Page title + Following / Followers (desktop only) -->
      <div class="nav-header__page-info" v-if="isUserAuth">
        <h1 class="nav-header__page-title">{{ pageTitle }}</h1>
        <div class="nav-header__connections" v-if="userdata">
          <span class="nav-header__connections-sep">|</span>
          <router-link
            :to="userdata?.id ? `/followers/${userdata.id}` : '/'"
            class="nav-header__conn-link"
          >
            <span class="nav-header__conn-count">{{ followingCount }}</span>
            <span class="nav-header__conn-label">Following</span>
          </router-link>
          <router-link
            :to="userdata?.id ? `/following/${userdata.id}` : '/'"
            class="nav-header__conn-link"
          >
            <span class="nav-header__conn-count">{{ followersCount }}</span>
            <span class="nav-header__conn-label">Followers</span>
          </router-link>
        </div>
      </div>
      <!-- Fallback title shown to logged-out visitors -->
      <div class="nav-header__page-info" v-else>
        <h1 class="nav-header__page-title">Community</h1>
      </div>

      <!-- Search Bar -->

      <!-- @input="fetchSearchResults" -->
      <div class="nav-header__search" v-if="isUserAuth">
        <div class="nav-header-search">
          <input
            type="text"
            v-model="searchText"
            @input="() => fetchSearchResults()"
            placeholder="Search..."
            class="nav-header-search__input"
            ref="searchInputRef"
            @click.stop
          />
          <!-- <button class="nav-header-search__btn" @click.stop="fetchSearchResults"> -->
          <button class="nav-header-search__btn" @click.stop="() => fetchSearchResults()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="8" stroke="white" stroke-width="2.2"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="white" stroke-width="2.2" stroke-linecap="round"/>
            </svg>
          </button>
          <!-- Search dropdown results -->
          <div
            v-if="isLoading || isSearchActive"
            ref="dropdownRef"
            class="nav-header-search__dropdown"
            @click.stop
          >
            <!-- Loading spinner while the debounced search request is in flight -->
            <div v-if="isLoading" class="flex justify-center items-center py-6">
              <ProgressSpinner style="width: 36px; height: 36px; margin: 0 auto" strokeWidth="5" fill="transparent" animationDuration=".8s" />
            </div>
            <!-- Once loaded, render grouped results: people, posts, groups -->
            <template v-else>
              <!-- People results -->
              <div>
                <h4 class="search-section-title">People ({{ allUsers.length }})</h4>
                <div v-if="allUsers.length">
                  <!-- One SuggestedPost row per matched user -->
                  <div v-for="user in displayedUsers" :key="user.id" class="mb-2">
                    <SuggestedPost :data="user" type="user" :onCloseDropdown="closeSearchDropdown" />
                  </div>
                </div>
                <!-- Empty-state message when no users match -->
                <p v-else class="search-empty-message">User not found</p>
              </div>
              <!-- Post results, only shown when there are matches -->
              <div v-if="allPosts.length" class="mt-3">
                <h4 class="search-section-title">Posts ({{ allPosts.length }})</h4>
                <div v-for="post in displayedPosts" :key="post.id" class="mb-2" @click="closeSearchDropdown">
                  <SuggestedPostCompact :post="post" :onCloseDropdown="closeSearchDropdown" />
                </div>
              </div>
              <!-- Group results -->
              <div class="mt-3">
                <h4 class="search-section-title">Groups ({{ allGroups.length }})</h4>
                <div v-if="allGroups.length">
                  <!-- One SuggestedGroupItem per matched group, with an inline follow button -->
                  <div v-for="group in limitedGroups" :key="group.id" class="mb-2" @click="closeSearchDropdown">
                    <SuggestedGroupItem :group="group" :showFollowButton="true" @follow="followGroup(group)" />
                  </div>
                </div>
                <!-- Empty-state message when no groups match -->
                <p v-else class="search-empty-message">Group not found</p>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Right section: authenticated -->
      <div class="nav-header__right" v-if="isUserAuth && userdata">

        <!-- Rightbar toggle — visible only at 640px–900px where widget column is hidden -->
        <button
          class="nav-header__rightbar-toggle"
          :class="{ 'nav-header__rightbar-toggle--active': activeMobile === 'right' }"
          @click="$emit('set-mobile', 'right')"
          aria-label="Open discover panel"
          v-tooltip.bottom="'Discover'"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="9"  cy="7"  r="4" stroke="currentColor" stroke-width="2"/>
            <circle cx="17" cy="9"  r="3" stroke="currentColor" stroke-width="2"/>
            <path d="M1 21v-1a8 8 0 0 1 16 0v1" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <path d="M17 13a6 6 0 0 1 6 6v1" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>

        <!-- Notification icon with circular background -->
        <router-link to="/notifications" class="notif-btn" :class="{ 'bell-shake': isShaking }">
          <span class="notif-btn__bg" :class="{ 'notif-btn__bg--active': isNotificationsPage }">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <!-- Unread count badge, only rendered when there's at least one unread notification; caps display at "99+" -->
          <span v-if="unreadNotificationsCount > 0" class="notif-btn__badge">
            {{ unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount }}
          </span>
        </router-link>

        <!-- Profile: name on left, avatar on right -->
        <!-- <Dropdown style="border: none !important"
          :pt="{ panel: { class: 'header-dropdown-panel' } }"
          class="current-org-drops"
          v-model="selectedProfile"
          :options="filteredOrgs"
          optionLabel="name"
          @change="setCurrentProfile(selectedProfile)"
          placeholder="Select profile"
        >
          <template #value="slotProps">
            <div v-if="slotProps.value" class="profile-trigger">
              <div class="profile-trigger__text">
                <span class="profile-trigger__name flex items-center gap-1">
                  <span class="truncate">
                    {{ slotProps.value.firstName || slotProps.value.username?.slice(0,15) }}
                    {{ slotProps.value.lastName || '' }}
                  </span>
                  <img v-if="badgeIconSrc" :src="badgeIconSrc" alt="badge" class="w-[14px] h-[14px] object-contain flex-shrink-0" />
                </span>
              </div>
              <BaseAvatarNew
                :storage-id="slotProps.value?.avatarFile?.storageId"
                classes="header-avatar"
                :rating="userdata?.rating"
              />
            </div>
            <span v-else class="profile-trigger__placeholder">{{ slotProps.placeholder }}</span>
          </template>
          <template #option="slotProps">
            <div class="flex items-center gap-2">
              <BaseAvatarNew :storage-id="slotProps.option?.avatarFile?.storageId" class="header-dropdown-avatar" :rating="slotProps.option?.rating" />
              <span class="dropdown-menu__caption">{{ slotProps.option.username }}</span>
            </div>
          </template>
        </Dropdown> -->
        <!-- Desktop profile link: username + badge + avatar → goes to profile page -->
                <!-- {{ userdata.firstName || userdata.username?.slice(0, 15) }} -->
        <div
          v-if="userdata"
          class="profile-trigger"
          style="cursor: pointer"
          @click="userdata.username && $router.push(`/user/${userdata.username}`)"
        >
          <div class="profile-trigger__text">
            <span class="profile-trigger__name flex items-center gap-1">
              <span class="profile-trigger__name-text">
                {{ userdata.firstName || userdata.username }}
                {{ userdata.lastName || '' }}
              </span>
              <img v-if="badgeIconSrc" :src="badgeIconSrc" alt="badge" class="w-[20px] h-[20px] object-contain flex-shrink-0" />
            </span>
          </div>
          <div style="position: relative; pointer-events: none">
            <BaseAvatarNew
              :storage-id="userdata?.avatarFile?.storageId"
              classes="header-avatar"
              :rating="userdata?.rating"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    <transition name="slide-down">
      <div v-if="isMenuOpen" class="mobile-menu sm:hidden flex flex-col items-center bg-[var(--color-theme-wise-background)] w-full shadow-lg py-4 z-[999]">
        <Dropdown style="border: none !important"
          :pt="{ panel: { class: 'mobile-dropdown-panel' } }"
          class="mobile-org-dropdown w-[80%]"
          v-model="selectedProfile"
          :options="filteredOrgs"
          optionLabel="name"
          @change="setCurrentProfile(selectedProfile)"
          placeholder="Select profile"
        >
          <!-- Customizes how the CURRENTLY SELECTED profile is displayed in the closed dropdown -->
          <template #value="slotProps">
            <div v-if="slotProps.value" class="flex items-center justify-center gap-2">
              <BaseAvatar :storage-id="slotProps.value?.avatarFile?.storageId" classes="header-dropdown-avatar" />
              <span class="dropdown-menu__caption !text-sm">{{ slotProps.value.username?.slice(0,20) }}</span>
            </div>
            <!-- Fallback shown before any profile is selected -->
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>
          <!-- Customizes how each OPTION ROW (org/profile) is rendered in the open dropdown panel -->
          <template #option="slotProps">
            <div class="flex items-center gap-2">
              <BaseAvatar :storage-id="slotProps.option?.avatarFile?.storageId" class="w-8 h-8" />
              <span class="truncate text-sm font-medium">{{ slotProps.option.username }}</span>
            </div>
          </template>
        </Dropdown>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, nextTick } from "vue";
import ProgressSpinner from "primevue/progressspinner";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import Dropdown from "primevue/dropdown";
import { useThemeScheme } from "@/composables/useThemeSchema";
import { makeLegacyDecoration } from "@/helpers/urlDecorationHelper.js";
import SuggestedPost from '../Post/SuggestedPost.vue';
import SuggestedPostCompact from '@/components/Post/SuggestedPostCompact.vue';
import SuggestedGroupItem from '@/components/Groups/SuggestedGroupItem.vue';
import { debounce } from "@/helpers/debounceHelper";
import LaunchPadLogo from '@/components/Icons/DougJonesMainLogo.vue';
import BaseAvatarNew from '@/components/ui-components/NewBaseAvatarNew/index.vue';
import { BaseURL } from '@/global/baseUrls.js';

// Mobile panel state — driven by default.vue via activeMobile prop.
// NavHeader emits 'set-mobile' with a key; default.vue toggles the value.
/**
 * @prop {string} [activeMobile] - Key of the currently open mobile panel ('left' | 'right' | 'analytics' | 'search' | ''), owned by the parent (default.vue).
 */
const props = defineProps<{ activeMobile?: string }>();
/**
 * @emits set-mobile - Fired with a panel key when a mobile icon is tapped; the parent toggles that panel open/closed.
 */
const emit = defineEmits<{
  (e: 'set-mobile', val: string): void
}>();

// Auto-focus mobile search input when search panel opens
/** Template ref to the mobile search panel's input element. */
const mobileSearchRef = ref<HTMLInputElement | null>(null);
/** Focuses the mobile search input whenever the 'search' mobile panel becomes active. */
watch(() => props.activeMobile, (val) => {
  if (val === 'search') nextTick(() => mobileSearchRef.value?.focus());
});

const store = useStore();
const route = useRoute();
const router = useRouter();

/** Whether the viewport is at or below the mobile/tablet breakpoint (1024px). */
const isMobile = ref(false);
/** Whether the current route is the home page. */
const isHomePage = computed(() => route.path === "/");
/** Recomputes `isMobile` from the current window width; intended to run on mount/resize. */
const checkIfMobile = () => (isMobile.value = window.innerWidth <= 1024);
/** Username-keyed profile data for the current user, from the store. */
const profileData = computed(() => store.getters.getUsernameData);
/** Dispatches the store's logOut action. */
const logOut = (payload) => store.dispatch("logOut", payload);
// dark them image logic
const { isDarkMode } = useThemeScheme();

/** Currently logged-in user object from the store. */
const userdata = computed(() => store.getters.getUser);
/** Whether a user is currently authenticated. */
const isUserAuth = computed(() => store.getters.getAuthStatus);
/** Organizations the current user belongs to. */
const myOrgs = computed(() => store.getters.getOnlyUserOrgs);
/** The org/profile currently selected as "active" for the user. */
const currentOrgProfile = computed(() => store.getters.getCurrentOrgProfile);
/** Whether the current route is the notifications page (used to highlight the bell icon). */
const isNotificationsPage = computed(() => route.path === "/notifications");
/** Unread notifications count from the store, shown in the bell badge. */
const unreadNotificationsCount = computed(() => store.getters.getUnreadNotificationsCount);
/** Whether the notification bell is currently mid bell-shake animation. */
const isShaking = ref(false);
/** Whether the mobile profile-switcher dropdown menu is open. */
const isMenuOpen = ref(false);
/** Whether a search request is currently in flight (drives the search dropdown's spinner). */
const isLoading = ref(false);
/** Orgs the user can switch into (filtered to non-reader, non-private roles), used by the profile switcher dropdown. */
const filteredOrgs = ref([]);
/** Currently selected profile/org in the profile switcher dropdown. */
const selectedProfile = ref(null);
/** Template ref to the desktop search dropdown, used to detect outside clicks and close it. */
const dropdownRef = ref<HTMLElement | null>(null);

/** Maps known badge icon keys to their CDN image URLs. */
const BADGE_ICON_MAP: Record<string, string> = {
  verified: "https://cdn.digicollect.com/cdn/launchpad/verified.png",
  heart: "https://cdn.digicollect.com/cdn/launchpad/heart.png",
  flame: "https://cdn.digicollect.com/cdn/launchpad/flame.png",
  star: "https://cdn.digicollect.com/cdn/launchpad/star.png",
  crown: "https://cdn.digicollect.com/cdn/launchpad/crown.png",
  sparkles: "https://cdn.digicollect.com/cdn/launchpad/sparkles.png",
};
/** Resolves the current profile's badge icon: a known key maps via BADGE_ICON_MAP, otherwise it's treated as a relative path off BaseURL. */
const badgeIconSrc = computed(() => { const k = selectedProfile.value?.userBadgeIcon ?? ""; return BADGE_ICON_MAP[k] || (k ? `${BaseURL}${k}` : ""); });
/** Template ref to the desktop search input element. */
const searchInputRef = ref<HTMLElement | null>(null);

// Page title mapping
/** Maps each route name to the header title text shown for that page. */
const pageTitleMap: Record<string, string> = {
  home: 'Community',
  search: 'Explore',
  groups: 'Groups',
  'user-groups': 'Groups',
  notifications: 'Notifications',
  referrals: 'Invite',
  profile: 'Profile',
  username: 'Profile',
  org: 'Group',
  'dougjones-help': 'Help',
  followers: 'Following',
  following: 'Followers',
  channel: 'Channel',
  post: 'Post',
  reply: 'Reply',
  aboutPage: 'Quick Links',
};

/** Header title for the current route, falling back to 'Community' for unmapped routes. */
const pageTitle = computed(() => {
  const name = route.name as string;
  return pageTitleMap[name] || 'Community';
});

// Following / Followers from current user data
/** Count of accounts the current user follows. */
const followingCount = computed(() => userdata.value?.numFollowing ?? 0);
/** Count of accounts following the current user. */
const followersCount = computed(() => userdata.value?.numTotalFollowers ?? 0);

// ----- Search -----
/** Two-way bound search query text, backed by the store (get reads state, set dispatches the update action). */
const searchText = computed({
  get: () => store.getters.getSearchText,
  set: value => store.dispatch('updateSearchText', value),
});

/** Full search results: matching users, from the store. */
const allUsers = computed(() => store.getters.getSearchUsers);
/** Full search results: matching posts, from the store. */
const allPosts = computed(() => store.getters.getSearchPosts);
/** Full search results: matching groups, from the store. */
const allGroups = computed(() => store.getters.getSearchGroups || []);
/** Max number of users/posts shown at once in the search dropdown. */
const pageSize = 5;
/** Users currently rendered in the search dropdown (first page of `allUsers`, refreshed by the `allUsers` watcher). */
const displayedUsers = ref([]);
/** Posts currently rendered in the search dropdown (first page of `allPosts`, refreshed by the `allPosts` watcher). */
const displayedPosts = ref([]);
// const limitedGroups = computed(() => allGroups.value.slice(0, 3));
/** Groups shown in the search dropdown (currently unlimited; previously capped at 3 — see commented line above). */
const limitedGroups = computed(() => allGroups.value);

// const fetchSearchResults = debounce(async () => {
// const fetchSearchResults: () => void = debounce(async () => {
//   if (!searchText.value || !searchText.value.trim()) {
//     store.commit('clearSearchResults');
//     resetSearchLists();
//     isLoading.value = false;
//     return;
//   }
//   isLoading.value = true;
//   try {
//     await store.dispatch("fetchSearchResults", searchText.value);
//   } finally {
//     isLoading.value = false;
//   }
// }, 600);
/** Debounced (600ms) search: dispatches `fetchSearchResults` for the current query, or clears results when the query is empty. */
const fetchSearchResults = debounce(async () => {
  if (!searchText.value || !searchText.value.trim()) {
    store.commit('clearSearchResults');
    resetSearchLists();
    isLoading.value = false;
    return;
  }
  isLoading.value = true;
  try {
    await store.dispatch("fetchSearchResults", searchText.value);
  } finally {
    isLoading.value = false;
  }
}, 600) as () => void;

/** Clears search results/state as soon as the query text becomes empty. */
watch(searchText, (val) => {
  if (!val || !val.trim()) {
    store.commit('clearSearchResults');
    resetSearchLists();
  }
});

/** Re-slices `displayedUsers` to the first page whenever the store's user results change. */
watch(allUsers, (val) => {
  if (searchText.value?.trim()) displayedUsers.value = val.slice(0, pageSize);
});

/** Re-slices `displayedPosts` to the first page whenever the store's post results change. */
watch(allPosts, (val) => {
  if (searchText.value?.trim()) displayedPosts.value = val.slice(0, pageSize);
});

// Previously required at least one non-empty result array, so a search with
// zero matches never rendered the dropdown at all — meaning "User not found" /
// "Group not found" could never be shown. Now it just tracks whether there's
// an active query; the empty-state messaging lives in the template instead.
const isSearchActive = computed(() => !!searchText.value?.trim());

/** Empties the displayed (paged) user and post result lists. */
function resetSearchLists() {
  displayedUsers.value = [];
  displayedPosts.value = [];
}

/**
 * Follows a group from the search results and marks it as followed locally on success.
 * @param {Object} group - Group result object; must have an `id`.
 */
async function followGroup(group) {
  try {
    const response = await store.dispatch('followGroup', group.id);
    if (response) group.following = true;
  } catch (e) {
    console.error('Error following group:', e);
  }
}

/** Clears the search query/results and closes the search dropdown. */
function closeSearchDropdown() {
  store.dispatch('updateSearchText', '');
  resetSearchLists();
}

// ----- Profile dropdown -----
/** Dispatches selecting a new "current" org/profile for the user. */
const setCurrentProfile = (data) => store.dispatch("fetchCurrentProfileOrg", data);

/** Dispatches fetching the orgs the current user belongs to, optionally filtered by search text. */
const fetchMyOrgs = (search = "") => store.dispatch("fetchOnlyUserOrgs", { search });

// ----- Notifications shake -----
/** Restarts the bell-shake animation by briefly toggling `isShaking` off then on (via requestAnimationFrame), auto-resetting after 500ms. */
const triggerNotificationShake = () => {
  isShaking.value = false;
  requestAnimationFrame(() => {
    isShaking.value = true;
    setTimeout(() => { isShaking.value = false; }, 500);
  });
};

/** Event handler for the global 'notifications:received' event — triggers the bell shake. */
const handleNotificationReceived = () => {
  triggerNotificationShake();
};

/** Shakes the bell whenever the unread count increases (a new notification arrived). */
watch(unreadNotificationsCount, (newCount, oldCount) => {
  if (newCount > oldCount && oldCount !== undefined) triggerNotificationShake();
});

/** Shakes the bell ~1s after a user first becomes available with pre-existing unread notifications (e.g. right after login). */
watch(() => userdata.value, (newUser, oldUser) => {
  if (newUser && !oldUser && unreadNotificationsCount.value > 0) {
    setTimeout(() => triggerNotificationShake(), 1000);
  }
});

/** Keeps the profile-switcher dropdown's selection in sync with the store's current org profile. */
watch(() => currentOrgProfile.value, () => {
  selectedProfile.value = currentOrgProfile.value;
});

// Old: onMounted only ran setup if userdata existed at mount time.
// After signup, userdata arrives async — watch for it and run the same setup.
/**
 * Runs the profile-switcher setup as soon as the user id first becomes available
 * (covers the post-signup case where userdata loads asynchronously after mount):
 * fetches the user's orgs, filters them to non-reader/non-private ones, prepends
 * the user's own profile, and seeds `selectedProfile`.
 */
watch(() => userdata.value?.id, async (newId, oldId) => {
  if (newId && !oldId) {
    await fetchMyOrgs();
    // OLD: filteredOrgs.value = myOrgs.value.filter(...) — crashed with
    // "Cannot read properties of undefined (reading 'filter')" when the orgs
    // API failed (FastAPI error shape has no .results). Guard with Array.isArray.
    filteredOrgs.value = (Array.isArray(myOrgs.value) ? myOrgs.value : []).filter(org => {
      const user = org.members?.find(us => us.userId === newId);
      return user?.role && user.role !== 'reader' && org.isPrivate === false;
    });
    filteredOrgs.value.unshift(userdata.value);
    // Old: currentOrgProfile is null for new users — selectedProfile stayed null, nothing showed
    selectedProfile.value = currentOrgProfile.value || userdata.value;
  }
});

// Click outside to close search dropdown
/** Document click handler: closes the desktop search dropdown when a click lands outside it. */
const handleSearchClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    closeSearchDropdown();
  }
};

/** Toggles the mobile profile-switcher dropdown menu open/closed. */
const toggleMobileMenu = () => { isMenuOpen.value = !isMenuOpen.value; };

/**
 * On mount: wires up the outside-click listener for the search dropdown and the
 * global notification-received listener, then — if a user is already logged in —
 * fetches their orgs and seeds the profile-switcher dropdown (filtered orgs +
 * own profile prepended, current org profile selected).
 */
onMounted(async () => {
  document.addEventListener('click', handleSearchClickOutside);
  window.addEventListener('notifications:received', handleNotificationReceived);
  if (userdata.value?.id) {
    await fetchMyOrgs();
    // OLD: filteredOrgs.value = myOrgs.value.filter(...) — same undefined
    // crash as the watcher above when the orgs API fails; guard with Array.isArray.
    filteredOrgs.value = (Array.isArray(myOrgs.value) ? myOrgs.value : []).filter(org => {
      const user = org.members?.find(us => us.userId === userdata.value?.id);
      return user?.role && user.role !== 'reader' && org.isPrivate === false;
    });
    filteredOrgs.value.unshift(userdata.value);
    // Old: currentOrgProfile is null for new users — selectedProfile stayed null, nothing showed
    selectedProfile.value = currentOrgProfile.value || userdata.value;
  }
});

/** Cleans up the outside-click and notification-received listeners registered in `onMounted`. */
onBeforeUnmount(() => {
  document.removeEventListener('click', handleSearchClickOutside);
  window.removeEventListener('notifications:received', handleNotificationReceived);
});
</script>

<style lang="scss" scoped>
.nav-header {
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 80px;
  max-height: var(--size-header-height);
  z-index: 1200;
  background: var(--color-app-background-gradient, linear-gradient(135deg, var(--color-background-primary) 0%, var(--color-background-secondary) 100%));
  // border-bottom: 1px solid var(--color-header-border-light-v2);
  border-bottom: none;
  color: var(--color-foreground-primary);
  flex-shrink: 0;

  &__inner {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 16px;
  }

  @media (max-width: 639px) {
    height: 56px;
    min-height: 56px;
    max-height: 56px;
    padding: 0;
    overflow: visible;
  }

  &__page-info {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
    min-width: 0;
  }

  &__page-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--color-quick-link-icon-bg);
    white-space: nowrap;
    margin: 0;
    line-height: 1;
    width: 200px;
    text-align: end;
  }

  &__connections {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__connections-sep {
    color: var(--color-foreground-primary);
    // opacity: 0.3;
    font-size: 1.2rem;
  }

  &__conn-link {
    display: flex;
    align-items: center;
    gap: 4px;
    text-decoration: none;
    color: var(--color-foreground-primary);
    font-size: 0.82rem;
    transition: color 0.18s ease;

    &:hover {
      color: var(--color-quick-link-icon-bg);
    }
  }

  &__conn-count {
    font-weight: 700;
    font-size: 0.88rem;
  }

  &__conn-label {
    opacity: 0.75;
  }

  &__search {
    flex: 1;
    // max-width: 420px;
    max-width: 320px;
    position: relative;
  }

  &__right {
    margin-left: auto;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 12px;
  }

}

// Rightbar toggle: hidden on desktop (>900px), visible at 640–900px where the widget column is hidden
.nav-header__rightbar-toggle {
  display: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--color-foreground-primary);
  transition: background 0.18s ease, color 0.18s ease;

  &:hover { background: var(--color-post-button-bg, rgba(0,0,0,0.06)); }

  &.nav-header__rightbar-toggle--active {
    background: var(--color-accent-primary);
    color: var(--color-text-black-white);
  }

  @media (max-width: 900px) and (min-width: 640px) {
    display: flex;
  }
}

// Direct-child selector: only hide desktop sections that are immediate children of
// nav-header__inner — this lets .nav-header__page-info inside the analytics panel still show.
@media (max-width: 639px) {
  .nav-header__inner > .nav-header__page-info,
  .nav-header__inner > .nav-header__search,
  .nav-header__inner > .nav-header__right {
    display: none;
  }
}

// Search bar
.nav-header-search {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  &__input {
    background-image: linear-gradient(to right, var(--color-quick-link-icon-bg) 2px,transparent 2px);
    background-repeat: no-repeat;
    background-position: 10px center;
    background-size: 2px 60%;
    width: 100%;
    height: 40px;
    // border-radius: 999px;
    border-radius: 5px;
    border: 1px solid var(--color-header-border-light-v2, rgba(0,0,0,0.12));
    // background: var(--color-app-card-bg, #fff);
    color: var(--color-foreground-primary);
    padding: 0 50px 0 18px;
    font-size: 13.5px;
    outline: none;
    transition: border-color 0.18s ease, box-shadow 0.18s ease;
    min-width: 180px;

    &::placeholder {
      color: var(--color-foreground-primary);
      opacity: 0.5;
    }

    &:focus {
      border-color: var(--color-quick-link-icon-bg);
      box-shadow: 0 0 0 3px rgba(4, 154, 140, 0.12);
    }
  }

  &__btn {
    position: absolute;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    // width: 34px;
    // height: 34px;
    // border-radius: 999px;
    width: 25px;
    height: 25px;
    border-radius: 5px;
    background: var(--color-quick-link-icon-bg);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.18s ease;

    &:hover { background: var(--color-quick-link-icon-bg); }
  }

  &__dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    width: 100%;
    max-height: 420px;
    overflow-y: auto;
    // background: var(--color-app-card-bg, #fff);
    background: var(--color-header-bg-light-v2);
    border: 1px solid var(--color-header-border-light-v2);
    border-radius: 14px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.12);
    z-index: 9999;
    padding: 10px;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }
}

.search-section-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--color-foreground-primary);
  opacity: 0.7;
  margin-bottom: 6px;
  padding-left: 4px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.search-empty-message {
  font-size: 13px;
  color: var(--color-text-02);
  opacity: 0.7;
  padding: 4px 4px 8px;
}

// Notification button
.notif-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: var(--color-foreground-primary);

  &__bg {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    // background: var(--color-app-card-bg, rgba(0,0,0,0.05));
    // border: 1px solid var(--color-header-border-light-v2, rgba(0,0,0,0.08));
    transition: background 0.18s ease, border-color 0.18s ease;

    &--active {
      background: var(--color-quick-link-icon-bg);
      border-color: var(--color-quick-link-icon-bg);
      color: var(--color-text-black-white);
    }

    &:hover {
      background: var(--color-quick-link-icon-bg);
      border-color: var(--color-quick-link-icon-bg);
      color: var(--color-text-black-white);
      // background: rgba(4, 154, 140, 0.1);
      // border-color: #001871;
      // color: #001871;
    }
  }

  &__badge {
    position: absolute;
    top: -2px;
    right: -2px;
    /* OLD: background: #E73C3E; */
    background: var(--color-error-text);
    /* OLD: color: #fff; */
    color: var(--color-white);
    font-size: 10px;
    font-weight: 700;
    border-radius: 999px;
    min-width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
  }
}

// Profile trigger
.current-org-drops {
  cursor: pointer;
}

.profile-trigger {
  display: flex;
  align-items: center;
  gap: 10px;

  &__text {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    min-width: 0;
    max-width: 300px;
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    color: var(--color-foreground-primary);
    white-space: normal;
    word-break: break-word;
    text-align: right;
  }

  &__name-text {
    min-width: 0;
  }

  &__sub {
    font-size: 12px;
    // color: #001871;
    color: var(--color-quick-link-icon-bg) !important;
    font-weight: 400;
  }

  &__placeholder {
    font-size: 13px;
    color: var(--color-foreground-primary);
    opacity: 0.6;
  }
}

// Bell shake animation
@keyframes bellShake {
  0%, 100% { transform: rotate(0deg); }
  10% { transform: rotate(-10deg); }
  20% { transform: rotate(10deg); }
  30% { transform: rotate(-10deg); }
  40% { transform: rotate(10deg); }
  50% { transform: rotate(-5deg); }
  60% { transform: rotate(5deg); }
  70% { transform: rotate(-5deg); }
  80% { transform: rotate(5deg); }
  90% { transform: rotate(0deg); }
}
.bell-shake { animation: bellShake 0.5s ease-in-out; }

// Slide-down mobile transition
.slide-down-enter-active, .slide-down-leave-active { transition: all 0.3s ease; }
.slide-down-enter-from, .slide-down-leave-to { opacity: 0; transform: translateY(-10px); }

.mobile-menu {
  position: absolute;
  top: 65px;
  left: 0;
  width: 100%;
  background: var(--color-theme-wise-background);
  border-top: 1px solid rgba(0,0,0,0.08);
  box-shadow: 0 4px 14px rgba(0,0,0,0.1);
  border-radius: 0 0 16px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  z-index: 999;
  padding: 1rem 0;
}

</style>

<style lang="scss">
// Header avatar size
.header-avatar .avatar,
.header-avatar .avatar-ring {
  width: 36px !important;
  height: 36px !important;
  // border-radius: 50% !important;
  border-radius: 8px !important;
}
.header-avatar .avatar-img {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  // border-radius: 50% !important;
  border-radius: 8px !important;
}

// Dropdown panel
.header-dropdown-panel .p-dropdown-item {
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
}
.header-dropdown-panel .header-dropdown-avatar .avatar {
  width: 28px !important;
  height: 28px !important;
  // border-radius: 50% !important;
  border-radius: 8px !important;
}
.header-dropdown-panel .header-dropdown-avatar .avatar-img {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  // border-radius: 50% !important;
  border-radius: 8px !important;
}

// Profile dropdown styling
.current-org-drops.p-dropdown {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  position: relative;
}
// Old: display: none hid the trigger so only a tiny area was clickable
// Now: invisible overlay covering the full dropdown area — PrimeVue's own click handler fires
.current-org-drops .p-dropdown-trigger {
  position: absolute !important;
  inset: 0 !important;
  width: 100% !important;
  height: 100% !important;
  opacity: 0 !important;
  cursor: pointer !important;
  z-index: 1 !important;
}
.current-org-drops .p-dropdown-label { padding: 0 !important; }
.current-org-drops.post-dropdown .p-dropdown-label { padding: 2px !important; }
// Username text
.current-org-drops .dropdown-menu__caption {
  color: var(--color-foreground-primary) !important;
}

// Mobile dropdown
.mobile-org-dropdown { width: 85%; max-width: 320px; font-weight: 500; }
.mobile-menu .p-dropdown {
  width: 100%;
  border-radius: 10px;
  /* OLD: background: var(--theme-wise-surface, #fff); */
  background: var(--theme-wise-surface, var(--color-white));
}
@media (max-width: 1024px) {
  .nav-header {
    &__page-title {
      width: 100% !important;
      text-align: start !important;
    }
  }
}


// Mobile top bar row — hidden on desktop, shown only at ≤639px
.nav-header__mobile {
  display: none;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  height: 56px;
  width: 100%;
  gap: 8px;

  @media (max-width: 639px) {
    display: flex;
  }
}

// Brand logo sits right next to the left hamburger
.nav-header__mob-logo {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-right: 4px;
}
.nav-header__mob-logo-img {
  height: 28px;
  width: auto;
}

// Right-side icon group — auto margin pushes it to the far right
.nav-header__mob-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
}

// Generic icon button used in mobile bar
.nav-header__mob-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--color-foreground-primary-v2);
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s, transform 0.1s;
  &:hover {
    background: var(--color-quick-link-icon-bg);
    border-color: var(--color-quick-link-icon-bg);
    color: var(--color-text-black-white);
  }
  &:active {
    background: var(--color-quick-link-icon-bg);
    border-color: var(--color-quick-link-icon-bg);
    color: var(--color-text-black-white);
    transform: scale(0.9);
  }
  &--active {
    background: var(--color-quick-link-icon-bg);
    border-color: var(--color-quick-link-icon-bg);
    color: var(--color-text-black-white);
  }
  // Inherit notif-btn badge positioning
  .notif-btn__badge {
    top: 2px;
    right: 2px;
  }
}

// Avatar + badge wrapper in mobile bar
.nav-header__mob-avatar {
  position: relative;
  width: 36px;
  height: 36px;
  cursor: pointer;
  flex-shrink: 0;
  .header-avatar, .avatar-ring, .avatar {
    width: 32px !important;
    height: 32px !important;
  }
}
.nav-header__mob-badge {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 18px;
  height: 18px;
  object-fit: contain;
  border-radius: 50%;
}

// Slide-down panels — fixed overlays below the 56px mobile bar
// position:fixed escapes app-shell__main's overflow:hidden so panels are never clipped
// MOBILE CHANGE 1a: background uses --color-header-bg-light-v2 (solid, theme-aware for light/dark)
.nav-header__mob-panel {
  position: fixed;
  top: 56px;
  left: 0;
  right: 0;
  z-index: 1200;
  width: 100%;
  padding: 12px 16px 14px;
  background: var(--color-header-bg-light-v2);
  border-top: 1px solid var(--color-button-border);
  border-bottom: 1px solid var(--color-button-border);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  &--search {
    padding: 8px 12px 12px;
  }
  // MOBILE CHANGE 1b: analytics panel — name and count align left
  .nav-header__page-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  .nav-header__page-title {
    width: auto;
    text-align: left;
  }
  .nav-header__connections-sep {
    display: none;
  }
  .nav-header__connections {
    flex-wrap: wrap;
    gap: 8px;
  }
}

// Full-width search input inside the panel
.nav-header-search--fullwidth {
  width: 100%;
  .nav-header-search__dropdown {
    width: 100%;
    left: 0;
    right: 0;
  }
}

// Slide transition for panels
.nav-header-slide-enter-active,
.nav-header-slide-leave-active {
  transition: transform 0.22s ease, opacity 0.18s ease;
}
.nav-header-slide-enter-from,
.nav-header-slide-leave-to {
  transform: translateY(-8px);
  opacity: 0;
}

// MOBILE CHANGE 1c: mobile avatar profile dropdown — override PrimeVue trigger sizing
// The trigger overlay covers the avatar area so tapping it opens the dropdown list.
.mob-profile-dropdown.current-org-drops {
  position: relative;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
}
// Panel appears below the mobile nav-header, full-width, scrollable
.mob-profile-panel {
  width: 220px !important;
  max-height: 300px;
  overflow-y: auto;
}
</style>
